import { Link } from "react-router-dom";
import { Music2, CalendarClock, Compass, CheckCircle2, X, AlertTriangle } from "lucide-react";

const DISMISS_KEY = "amds_orientation_dismissed_v1";

export function isOrientationDismissed() {
  try { return window.localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
}

export function dismissOrientation() {
  try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch {}
}

/**
 * F011 — Orientation Dashboard.
 * Persistent checklist at the top of /home. Auto-hides once all 3 items are
 * complete; the user can also dismiss it permanently (per-device).
 *
 * Items:
 *  1. Add your main instrument  (musicians only — bookers/conductors auto-skip)
 *  2. Add your first diary event
 *  3. Take the diary tour       (= visit /diary once; we mark it complete when they have any availability OR they click the link)
 */
export function OrientationCard({ onboarding, onDismiss }) {
  if (!onboarding) return null;
  const isMusician = onboarding.role === "musician";

  // Musicians need an instrument; bookers/conductors don't have that step.
  const items = [];
  if (isMusician) {
    items.push({
      key: "instrument",
      done: !!onboarding.has_instrument,
      icon: Music2,
      title: "Add your main instrument",
      body: "This is how your diary gets matched when bookers go looking for cover.",
      to: "/profile",
      cta: "Open profile",
    });
  }
  items.push({
    key: "availability",
    done: !!onboarding.has_availability,
    icon: CalendarClock,
    title: "Add your first diary event",
    body: "A concert, rehearsal, teaching slot — or a day off. Availability follows from what's in your diary.",
    to: "/diary",
    cta: "Open diary",
  });
  items.push({
    key: "tour",
    done: !!onboarding.has_availability,  // we treat "marked availability" as having seen the diary
    icon: Compass,
    title: isMusician ? "See who's looking for deps" : "Try the dep board",
    body: isMusician
      ? "The dep board shows live calls for your section."
      : "Send a quick dep broadcast to your contacts when you need cover.",
    to: "/deps",
    cta: "Open dep board",
  });

  const doneCount = items.filter((i) => i.done).length;
  // Auto-hide once everything is done.
  if (doneCount === items.length) return null;

  return (
    <section className="card-base p-5 border border-sage-200 bg-sage-50/40" data-testid="orientation-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="label-overline text-sage-700">Set up your diary</div>
          <h2 className="font-heading text-lg font-semibold mt-0.5">
            {doneCount}/{items.length} complete
          </h2>
          <p className="text-xs text-ink-600 mt-1">
            Set up your diary so it works for you — and so bookers can find you.
          </p>
        </div>
        <button
          onClick={onDismiss}
          className="btn-ghost !p-1.5"
          title="Dismiss orientation"
          data-testid="orientation-dismiss"
        >
          <X size={16} />
        </button>
      </div>

      <ul className="space-y-2">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.key} data-testid={`orientation-item-${item.key}`}>
              <Link
                to={item.to}
                className={`rounded-xl border p-3 flex items-center gap-3 transition ${
                  item.done
                    ? "border-sage-200 bg-sage-50/70"
                    : "border-ivory-200 bg-white hover:bg-ivory-50"
                }`}
              >
                <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${
                  item.done ? "bg-sage-500 text-white" : "bg-ivory-100 text-sage-600"
                }`}>
                  {item.done ? <CheckCircle2 size={16} /> : <Icon size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-sm ${item.done ? "line-through text-ink-500" : ""}`}>
                    {item.title}
                  </div>
                  <div className="text-xs text-ink-500 truncate">{item.body}</div>
                </div>
                {!item.done && (
                  <span className="text-xs text-sage-700 font-medium whitespace-nowrap">
                    {item.cta} →
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * F012 — Profile completion banner.
 * Non-dismissable while the gap exists. Only shown when the orientation card
 * has been dismissed AND the musician has no instrument set.
 */
export function ProfileBanner() {
  return (
    <Link
      to="/profile"
      className="block rounded-xl border border-terracotta-200 bg-terracotta-50/70 p-3 flex items-center gap-3 hover:bg-terracotta-50 transition"
      data-testid="profile-banner"
    >
      <div className="w-9 h-9 rounded-xl grid place-items-center bg-terracotta-100 text-terracotta-700 shrink-0">
        <AlertTriangle size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">Add your main instrument</div>
        <div className="text-xs text-ink-600 truncate">
          Bookers can't reach you until your instrument is set.
        </div>
      </div>
      <span className="text-xs text-terracotta-700 font-medium whitespace-nowrap">Fix →</span>
    </Link>
  );
}

/**
 * F013 — Availability banner.
 * Non-dismissable while the gap exists. Only shown when the orientation card
 * has been dismissed AND the user has no availability records.
 */
export function AvailabilityBanner() {
  return (
    <Link
      to="/diary"
      className="block rounded-xl border border-amber-200 bg-amber-50/70 p-3 flex items-center gap-3 hover:bg-amber-50 transition"
      data-testid="availability-banner"
    >
      <div className="w-9 h-9 rounded-xl grid place-items-center bg-amber-100 text-amber-deep shrink-0">
        <CalendarClock size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">Add a diary event</div>
        <div className="text-xs text-ink-600 truncate">
          Your diary unlocks dep matches — start with one entry.
        </div>
      </div>
      <span className="text-xs text-amber-deep font-medium whitespace-nowrap">Open diary →</span>
    </Link>
  );
}
